import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-premium";
import {
    TransportOrderNotificationListItemDto,
    TransportOrderNotificationState,
} from "../../models/transport/transportOrderNotification";
import { getDateTime } from "../transport/components/transportUtils";
import { MuiDataGrid } from "../framework/muiDataGrid";
import { transportOrderStateNames } from "../../models/transport/transportOrder";
import { useAppSelector } from "../../framework/customStore";
import {
    confirmAllMobileToDesktopNotifications,
    confirmTransportOrderNotification,
} from "../../services/transportOrderService";
import {
    confirmNotification,
    confirmAllDesktopNotifications,
} from "../../services/notificationService";
import type { ApiResponse } from "../../services/apiClient";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import {
    NotificationListItemDto,
    NotificationState,
} from "../../models/notification/notification";
import { showApiError } from "../framework/formUtils";

type NotificationSource = "transport" | "desktop";

interface UnifiedNotificationRow {
    id: string;
    source: NotificationSource;
    createdAt: string;
    creatorEmployeeId?: string;
    message: string;
    isConfirmed: boolean;
    // Transport-specific
    transportNotification?: TransportOrderNotificationListItemDto;
    // Desktop-specific
    desktopNotification?: NotificationListItemDto;
}

interface NotificationsTableProps {
    notifications: TransportOrderNotificationListItemDto[];
    desktopNotifications: NotificationListItemDto[];
    fetchNotifications: () => Promise<unknown>;
    hasTransportOrdersEnabled: boolean;
}

interface ContextData {
    vehicleRegisterNumber?: string;
    maintenanceTitle?: string;
    reminderDate?: string;
}

const parseContextJson = (contextJson: string): ContextData => {
    try {
        return JSON.parse(contextJson) as ContextData;
    } catch {
        return {};
    }
};

export const NotificationsTable = ({
    notifications,
    desktopNotifications,
    fetchNotifications,
    hasTransportOrdersEnabled,
}: NotificationsTableProps) => {
    const { t } = useTranslation();

    const confirmTransportOrderNotificationSafe: (
        id: string
    ) => Promise<ApiResponse<unknown>> = confirmTransportOrderNotification;

    const [confirmAllDisabled, setConfirmAllDisabled] = useState(false);
    const [acknowledgingIds, setAcknowledgingIds] = useState<Set<string>>(
        new Set()
    );
    const employees = useAppSelector((state) => state.employees.employees);

    const getEmployeeName = useCallback(
        (id: string) => {
            const employee = employees.find((i) => i.id === id);
            return employee ? employee.fullName : "-";
        },
        [employees]
    );

    const unifiedRows: UnifiedNotificationRow[] = useMemo(() => {
        const desktopRows: UnifiedNotificationRow[] = desktopNotifications.map(
            (n) => ({
                id: `desktop-${n.notificationId}`,
                source: "desktop" as NotificationSource,
                createdAt: n.createdAt,
                message: "",
                isConfirmed: n.state === NotificationState.Confirmed,
                desktopNotification: n,
            })
        );

        const transportRows: UnifiedNotificationRow[] =
            hasTransportOrdersEnabled
                ? notifications.map((n) => ({
                    id: `transport-${n.id}`,
                    source: "transport" as NotificationSource,
                    createdAt: n.createdDateTime,
                    creatorEmployeeId: n.createdEmployeeId,
                    message: "",
                    isConfirmed:
                          n.state ===
                          TransportOrderNotificationState.Confirmed.valueOf(),
                    transportNotification: n,
                }))
                : [];

        return [...desktopRows, ...transportRows].sort(
            (a, b) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime()
        );
    }, [desktopNotifications, notifications, hasTransportOrdersEnabled]);

    const handleConfirmAll = useCallback(async () => {
        setConfirmAllDisabled(true);
        try {
            const promises: Promise<unknown>[] = [];

            if (hasTransportOrdersEnabled && notifications.length > 0) {
                promises.push(confirmAllMobileToDesktopNotifications());
            }

            if (desktopNotifications.length > 0) {
                promises.push(confirmAllDesktopNotifications());
            }

            await Promise.all(promises);

            await fetchNotifications();
        } catch (error) {
            console.error("Error confirming all notifications:", error);
            showApiError(t("notifications.acknowledgeFailed"));
        } finally {
            setConfirmAllDisabled(false);
        }
    }, [
        hasTransportOrdersEnabled,
        notifications,
        desktopNotifications,
        fetchNotifications,
        t,
    ]);

    const handleAcknowledge = useCallback(
        async (row: UnifiedNotificationRow) => {
            setAcknowledgingIds((prev) => {
                const next = new Set(prev);
                next.add(row.id);
                return next;
            });

            try {
                if (row.source === "transport" && row.transportNotification) {
                    await confirmTransportOrderNotificationSafe(
                        row.transportNotification.id
                    );
                    await fetchNotifications();
                } else if (
                    row.source === "desktop" &&
                    row.desktopNotification
                ) {
                    await confirmNotification(
                        row.desktopNotification.notificationId,
                        {
                            targetObjectType: "Owner",
                            targetObjectId: row.desktopNotification.ownerId,
                        }
                    );
                    await fetchNotifications();
                }
            } catch (error) {
                console.error("Error acknowledging notification:", error);
                showApiError(t("notifications.acknowledgeFailed"));
            } finally {
                setAcknowledgingIds((prev) => {
                    const next = new Set(prev);
                    next.delete(row.id);
                    return next;
                });
            }
        },
        [fetchNotifications, t]
    );

    const columns: GridColDef<UnifiedNotificationRow>[] = useMemo(
        () => [
            {
                headerName: t("common.created"),
                field: "createdAt",
                width: 150,
                renderCell: (
                    params: GridRenderCellParams<UnifiedNotificationRow>
                ) => <div>{getDateTime(params.row?.createdAt)}</div>,
            },
            {
                headerName: t("transport.order.creator"),
                field: "creatorEmployeeId",
                sortable: false,
                flex: 1,
                renderCell: (
                    params: GridRenderCellParams<UnifiedNotificationRow>
                ) => {
                    if (params.row?.source === "transport") {
                        return (
                            <div>
                                {getEmployeeName(params.row?.creatorEmployeeId)}
                            </div>
                        );
                    }
                    return <div>-</div>;
                },
            },
            {
                headerName: t("common.message"),
                field: "message",
                sortable: false,
                flex: 4,
                cellClassName: "wrapCell",
                renderCell: (
                    params: GridRenderCellParams<UnifiedNotificationRow>
                ) => {
                    const row = params.row;

                    if (row?.source === "desktop" && row.desktopNotification) {
                        const context = parseContextJson(
                            row.desktopNotification.contextJson
                        );
                        return (
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    width: "100%",
                                }}
                            >
                                <Typography variant="body2">
                                    {t(row.desktopNotification.titleKey)}
                                </Typography>
                                {(context.vehicleRegisterNumber ||
                                    context.maintenanceTitle) && (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {[
                                            context.vehicleRegisterNumber,
                                            context.maintenanceTitle,
                                            context.reminderDate,
                                        ]
                                            .filter(Boolean)
                                            .join(" — ")}
                                    </Typography>
                                )}
                            </Box>
                        );
                    }

                    if (
                        row?.source === "transport" &&
                        row.transportNotification
                    ) {
                        const n = row.transportNotification;
                        const driverComment =
                            typeof n.driverComment === "string"
                                ? String(n.driverComment)
                                : "";

                        const orderStateKey =
                            n.orderState !== undefined && n.orderState !== null
                                ? (transportOrderStateNames[
                                    n.orderState
                                ] as unknown as string | undefined)
                                : undefined;

                        return (
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    width: "100%",
                                }}
                            >
                                <Typography variant="body2">
                                    {t(n.message, {
                                        orderNro: n.transportOrderNumber,
                                        vehicleName: n.vehicleRegisterNumber,
                                        state: orderStateKey
                                            ? t(orderStateKey)
                                            : "-",
                                        employeeName: getEmployeeName(
                                            n.createdEmployeeId
                                        ),
                                    })}
                                </Typography>

                                {driverComment.trim().length > 0 && (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "flex-start",
                                            gap: 0.75,
                                            mt: 0.5,
                                        }}
                                    >
                                        <ChatBubbleOutlineIcon
                                            sx={{
                                                fontSize: 16,
                                                mt: "2px",
                                                color: "text.secondary",
                                            }}
                                        />
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ whiteSpace: "pre-wrap" }}
                                        >
                                            {driverComment}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        );
                    }

                    return <div>-</div>;
                },
            },
            {
                headerName: t("common.status"),
                field: "isConfirmed",
                sortable: false,
                flex: 1,
                renderCell: (
                    params: GridRenderCellParams<UnifiedNotificationRow>
                ) => (
                    <div>
                        {params.row?.isConfirmed
                            ? t("notifications.confirmed")
                            : t("notifications.unconfirmed")}
                    </div>
                ),
            },
            {
                headerName: "",
                field: "acknowledge",
                sortable: false,
                filterable: false,
                width: 140,
                align: "right",
                headerAlign: "right",
                renderCell: (
                    params: GridRenderCellParams<UnifiedNotificationRow>
                ) => {
                    const row = params.row;
                    const isAcknowledging = row
                        ? acknowledgingIds.has(row.id)
                        : false;

                    return (
                        <Button
                            id={`acknowledge-notification-${row?.id}`}
                            size="small"
                            variant="contained"
                            color="success"
                            disabled={!row || isAcknowledging}
                            onClick={() => {
                                if (row) {
                                    void handleAcknowledge(row);
                                }
                            }}
                        >
                            {t("notifications.acknowledge")}
                        </Button>
                    );
                },
            },
        ],
        [acknowledgingIds, employees, getEmployeeName, handleAcknowledge, t]
    );

    return (
        <MuiDataGrid
            rows={unifiedRows}
            columns={columns}
            density="compact"
            rowSelection={false}
            disableColumnFilter
            getRowHeight={() => "auto"}
            getEstimatedRowHeight={() => 56}
            sx={{
                "& .MuiDataGrid-cell": {
                    alignItems: "flex-start",
                    py: 1,
                },
                "& .MuiDataGrid-cell.wrapCell": {
                    whiteSpace: "normal",
                    lineHeight: 1.35,
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                },
            }}
            toolbarProps={{
                title: t("notifications.tableHeader"),
                enableColumnSelector: false,
                disableQuickFilter: true,
                disableToolbarColumnsButton: true,
                customToolbarButtons:
                    unifiedRows.length > 0
                        ? [
                            {
                                key: "confirm-all",
                                color: "success",
                                variant: "contained",
                                onClick: handleConfirmAll,
                                children: t("notifications.confirmAll"),
                                disabled: confirmAllDisabled,
                                sx: { marginRight: 1 },
                            },
                        ]
                        : null,
            }}
        />
    );
};
